// src/pages/billing/BillingPage.jsx - Fixed calculations
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import DashboardLayout from '../../components/layout/DashboardLayout';
import StatsCard from '../../components/dashboard/StatsCard';
import InvoiceTable from '../../components/billing/InvoiceTable';
import PurchaseInvoiceTable from '../../components/billing/PurchaseInvoiceTable';
import QuotationTable from '../../components/billing/QuotationTable';
import CreateInvoiceModal from '../../components/billing/CreateInvoiceModal';
import CreatePurchaseInvoiceModal from '../../components/billing/CreatePurchaseInvoiceModal';
import CreateQuotationModal from '../../components/billing/CreateQuotationModal';
import { getInvoices, getBillingStats } from '../../services/billingService';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';

const BillingPage = () => {
  const { currentUser, vendorProfile } = useAuth();
  const { showAlert } = useNotification();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // State management
  const [allInvoices, setAllInvoices] = useState([]);
  const [allPurchaseInvoices, setAllPurchaseInvoices] = useState([]);
  const [allQuotations, setAllQuotations] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [filteredPurchaseInvoices, setFilteredPurchaseInvoices] = useState([]);
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const [stats, setStats] = useState(null);
  const [purchaseStats, setPurchaseStats] = useState(null);
  const [quotationStats, setQuotationStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [quotationLoading, setQuotationLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('sales');
  
  // Modal states
  const [showCreateSalesModal, setShowCreateSalesModal] = useState(false);
  const [showCreatePurchaseModal, setShowCreatePurchaseModal] = useState(false);
  const [showCreateQuotationModal, setShowCreateQuotationModal] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    dateRange: 'all'
  });

  const [purchaseFilters, setPurchaseFilters] = useState({
    status: '',
    search: '',
    dateRange: 'all'
  });

  const [quotationFilters, setQuotationFilters] = useState({
    search: '',
    dateRange: 'all'
  });

  // Handle URL parameters for tab switching and modal opening
  useEffect(() => {
    const tab = searchParams.get('tab');
    const type = searchParams.get('type');
    
    if (tab === 'purchases') {
      setActiveTab('purchases');
    } else if (tab === 'quotations') {
      setActiveTab('quotations');
    } else {
      setActiveTab('sales');
    }

    // Handle create modal opening from URL
    if (type === 'sales') {
      setShowCreateSalesModal(true);
      navigate('/billing', { replace: true });
    } else if (type === 'purchase') {
      setShowCreatePurchaseModal(true);
      setActiveTab('purchases');
      navigate('/billing?tab=purchases', { replace: true });
    } else if (type === 'quotation') {
      setShowCreateQuotationModal(true);
      setActiveTab('quotations');
      navigate('/billing?tab=quotations', { replace: true });
    }
  }, [searchParams, navigate]);

  // Helper function to calculate CGST and SGST - FIXED
  const calculateCGSTSGST = (taxableAmount, gstRate) => {
    const totalGSTAmount = (taxableAmount * gstRate) / 100;
    const cgstAmount = totalGSTAmount / 2;
    const sgstAmount = totalGSTAmount / 2;
    return { cgstAmount, sgstAmount, totalGSTAmount };
  };

  // Filter invoices based on search and date range
  const filterInvoices = (invoices, searchFilters) => {
    let filtered = [...invoices];

    // Apply search filter
    if (searchFilters.search && searchFilters.search.trim() !== '') {
      const searchTerm = searchFilters.search.toLowerCase().trim();
      filtered = filtered.filter(invoice => {
        const invoiceNumber = invoice.invoiceNumber?.toLowerCase() || invoice.quotationNumber?.toLowerCase() || '';
        const customerName = invoice.customerName?.toLowerCase() || '';
        const buyerName = invoice.buyerName?.toLowerCase() || '';
        const supplierName = invoice.supplierName?.toLowerCase() || '';
        const customerEmail = invoice.customerEmail?.toLowerCase() || '';
        const supplierEmail = invoice.supplierEmail?.toLowerCase() || '';
        
        return invoiceNumber.includes(searchTerm) || 
               customerName.includes(searchTerm) || 
               buyerName.includes(searchTerm) ||
               supplierName.includes(searchTerm) ||
               customerEmail.includes(searchTerm) ||
               supplierEmail.includes(searchTerm);
      });
    }

    // Apply status filter for quotations
    if (searchFilters.status && searchFilters.status !== '') {
      if (searchFilters.status === 'expired') {
        const now = new Date();
        filtered = filtered.filter(item => {
          if (item.validUntil) {
            const validDate = item.validUntil?.toDate ? item.validUntil.toDate() : new Date(item.validUntil);
            return validDate < now;
          }
          return false;
        });
      } else {
        filtered = filtered.filter(invoice => invoice.status === searchFilters.status);
      }
    }

    // Apply date range filter
    if (searchFilters.dateRange !== 'all') {
      const now = new Date();
      let daysBack = 0;

      switch (searchFilters.dateRange) {
        case 'today':
          daysBack = 0;
          break;
        case 'last7':
          daysBack = 7;
          break;
        case 'last30':
          daysBack = 30;
          break;
        case 'last90':
          daysBack = 90;
          break;
        case 'last365':
          daysBack = 365;
          break;
        default:
          daysBack = 0;
      }

      if (daysBack >= 0) {
        const startDate = new Date();
        if (daysBack === 0) {
          startDate.setHours(0, 0, 0, 0);
        } else {
          startDate.setDate(startDate.getDate() - daysBack);
          startDate.setHours(0, 0, 0, 0);
        }

        filtered = filtered.filter(invoice => {
          let invoiceDate;
          
          if (invoice.createdAt?.toDate) {
            invoiceDate = invoice.createdAt.toDate();
          } else if (invoice.createdAt?.seconds) {
            invoiceDate = new Date(invoice.createdAt.seconds * 1000);
          } else if (invoice.createdAt) {
            invoiceDate = new Date(invoice.createdAt);
          } else {
            return false;
          }
          
          if (isNaN(invoiceDate.getTime())) {
            return false;
          }
          
          return invoiceDate >= startDate;
        });
      }
    }

    return filtered;
  };

  // Calculate stats from filtered invoices - FIXED CALCULATIONS
  const calculateStats = (invoices) => {
    let totalAmount = 0;
    let totalTaxAmount = 0;
    let totalDiscountAmount = 0;

    invoices.forEach(invoice => {
      const discountPercentage = parseFloat(invoice.discount) || 0;
      const subtotal = invoice.subtotal || 0;
      const discountAmt = (subtotal * discountPercentage) / 100;
      totalDiscountAmount += discountAmt;

      if (invoice.invoiceType === 'with_gst') {
        // Calculate GST based on items
        let invoiceTaxAmount = 0;
        if (invoice.items && Array.isArray(invoice.items)) {
          invoice.items.forEach(item => {
            const itemDiscountAmount = (item.amount || 0) * discountPercentage / 100;
            const itemTaxableAmount = (item.amount || 0) - itemDiscountAmount;
            const { totalGSTAmount } = calculateCGSTSGST(itemTaxableAmount, item.gstRate || 0);
            invoiceTaxAmount += totalGSTAmount;
          });
        } else {
          // Fallback to stored tax amount
          invoiceTaxAmount = invoice.totalTaxAmount || 0;
        }
        totalTaxAmount += invoiceTaxAmount;
        totalAmount += (subtotal - discountAmt + invoiceTaxAmount);
      } else {
        totalAmount += (subtotal - discountAmt);
      }
    });
    
    return {
      totalAmount: totalAmount,
      paidAmount: totalAmount,
      pendingAmount: 0,
      totalInvoices: invoices.length,
      paidInvoices: invoices.length,
      pendingInvoices: 0,
      totalTaxAmount: totalTaxAmount,
      totalDiscountAmount: totalDiscountAmount
    };
  };

  // Calculate purchase stats from filtered purchase invoices - FIXED CALCULATIONS
  const calculatePurchaseStats = (invoices) => {
    let totalPurchaseAmount = 0;
    let totalGSTPaid = 0;

    invoices.forEach(invoice => {
      if (invoice.gstType === 'with_gst') {
        // Calculate GST based on items
        let invoiceGstAmount = 0;
        if (invoice.items && Array.isArray(invoice.items)) {
          invoice.items.forEach(item => {
            const itemAmount = parseFloat(item.amount) || 0;
            const { totalGSTAmount } = calculateCGSTSGST(itemAmount, item.gstRate || 0);
            invoiceGstAmount += totalGSTAmount;
          });
        } else {
          // Fallback to stored GST amount
          invoiceGstAmount = invoice.totalGstAmount || 0;
        }
        totalGSTPaid += invoiceGstAmount;
        totalPurchaseAmount += (invoice.subtotal || 0) + invoiceGstAmount;
      } else {
        totalPurchaseAmount += (invoice.subtotal || 0);
      }
    });

    const totalPurchaseInvoices = invoices.length;
    const pendingPurchases = invoices.filter(invoice => invoice.status === 'pending').length;
    const receivedPurchases = invoices.filter(invoice => invoice.status === 'received').length;

    return {
      totalAmount: totalPurchaseAmount,
      totalInvoices: totalPurchaseInvoices,
      pendingInvoices: pendingPurchases,
      receivedInvoices: receivedPurchases,
      totalGSTPaid: totalGSTPaid
    };
  };

  // Calculate quotation stats
  const calculateQuotationStats = (quotations) => {
    const totalAmount = quotations.reduce((sum, quotation) => sum + (quotation.totalAmount || 0), 0);
    const totalQuotations = quotations.length;
    const activeQuotations = quotations.filter(q => {
      const now = new Date();
      const validDate = q.validUntil?.toDate ? q.validUntil.toDate() : new Date(q.validUntil);
      return validDate >= now && q.status === 'active';
    }).length;
    const expiredQuotations = quotations.filter(q => {
      const now = new Date();
      const validDate = q.validUntil?.toDate ? q.validUntil.toDate() : new Date(q.validUntil);
      return validDate < now;
    }).length;
    const convertedQuotations = quotations.filter(q => q.status === 'converted').length;

    return {
      totalAmount,
      totalQuotations,
      activeQuotations,
      expiredQuotations,
      convertedQuotations
    };
  };

  // Apply filters and calculate stats when filters change
  useEffect(() => {
    const filtered = filterInvoices(allInvoices, filters);
    setFilteredInvoices(filtered);
    
    const calculatedStats = calculateStats(filtered);
    setStats(calculatedStats);
  }, [allInvoices, filters]);

  useEffect(() => {
    const filtered = filterInvoices(allPurchaseInvoices, purchaseFilters);
    setFilteredPurchaseInvoices(filtered);
    
    const calculatedPurchaseStats = calculatePurchaseStats(filtered);
    setPurchaseStats(calculatedPurchaseStats);
  }, [allPurchaseInvoices, purchaseFilters]);

  useEffect(() => {
    const filtered = filterInvoices(allQuotations, quotationFilters);
    setFilteredQuotations(filtered);
    
    const calculatedQuotationStats = calculateQuotationStats(filtered);
    setQuotationStats(calculatedQuotationStats);
  }, [allQuotations, quotationFilters]);

  // Load sales invoices data
  const loadSalesData = async () => {
    try {
      setLoading(true);
      const invoicesData = await getInvoices({});
      setAllInvoices(invoicesData);
    } catch (error) {
      console.error('Error loading sales billing data:', error);
      showAlert('Failed to load sales billing data', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Load purchase invoices data
  const loadPurchaseData = async () => {
    if (!vendorProfile?.uid) return;

    try {
      setPurchaseLoading(true);
      
      let purchaseQuery = query(
        collection(db, 'purchaseInvoices'),
        where('vendorId', '==', vendorProfile.uid)
      );

      const purchaseSnapshot = await getDocs(purchaseQuery);
      let purchaseInvoicesData = purchaseSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      purchaseInvoicesData.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB.getTime() - dateA.getTime();
      });

      setAllPurchaseInvoices(purchaseInvoicesData);
    } catch (error) {
      console.error('Error loading purchase data:', error);
      showAlert('Failed to load purchase data', 'error');
    } finally {
      setPurchaseLoading(false);
    }
  };

  // Load quotation data
  const loadQuotationData = async () => {
    if (!vendorProfile?.uid) return;

    try {
      setQuotationLoading(true);
      
      let quotationQuery = query(
        collection(db, 'quotations'),
        where('vendorId', '==', vendorProfile.uid)
      );

      const quotationSnapshot = await getDocs(quotationQuery);
      let quotationsData = quotationSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      quotationsData.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
        return dateB.getTime() - dateA.getTime();
      });

      setAllQuotations(quotationsData);
    } catch (error) {
      console.error('Error loading quotation data:', error);
      showAlert('Failed to load quotation data', 'error');
    } finally {
      setQuotationLoading(false);
    }
  };

  // Initial data loading
  useEffect(() => {
    if (currentUser) {
      loadSalesData();
      loadPurchaseData();
      loadQuotationData();
    }
  }, [currentUser]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Handle navigation to create invoice page
  const handleCreateInvoice = () => {
    navigate('/billing/create?type=sales');
  };

  // Handle tab switching
  const handleTabSwitch = (tab) => {
    setActiveTab(tab);
    navigate(`/billing?tab=${tab}`, { replace: true });
  };

  // Handle invoice creation success
  const handleInvoiceCreated = (invoice) => {
    setShowCreateSalesModal(false);
    loadSalesData();
    showAlert('Sales invoice created successfully!', 'success');
  };

  // Handle purchase invoice creation success
  const handlePurchaseInvoiceCreated = (invoice) => {
    setShowCreatePurchaseModal(false);
    loadPurchaseData();
    showAlert('Purchase invoice created successfully!', 'success');
  };

  // Handle quotation creation success
  const handleQuotationCreated = (quotation) => {
    setShowCreateQuotationModal(false);
    loadQuotationData();
    showAlert('Quotation created successfully!', 'success');
  };

  // Handle navigation to add product (for modals)
  const handleNavigateToAddProduct = () => {
    navigate('/inventory/add');
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Billing & Invoices</h1>
              <p className="mt-1 text-sm text-gray-600">
                Manage your sales invoices, purchase invoices, and quotations
              </p>
            </div>
            
            <button
              onClick={handleCreateInvoice}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
            >
              <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Create Invoice
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mt-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => handleTabSwitch('sales')}
                className={`py-2 px-1 border-b-2 font-medium text-sm cursor-pointer ${
                  activeTab === 'sales'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Sales Invoices
                {stats && (
                  <span className="ml-2 bg-green-100 text-green-600 py-0.5 px-2 rounded-full text-xs">
                    {stats.totalInvoices}
                  </span>
                )}
              </button>
              <button
                onClick={() => handleTabSwitch('purchases')}
                className={`py-2 px-1 border-b-2 font-medium text-sm cursor-pointer ${
                  activeTab === 'purchases'
                    ? 'border-orange-500 text-orange-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Purchase Invoices
                {purchaseStats && (
                  <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                    {purchaseStats.totalInvoices}
                  </span>
                )}
              </button>
              <button
                onClick={() => handleTabSwitch('quotations')}
                className={`py-2 px-1 border-b-2 font-medium text-sm cursor-pointer ${
                  activeTab === 'quotations'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Quotations
                {quotationStats && (
                  <span className="ml-2 bg-purple-100 text-purple-600 py-0.5 px-2 rounded-full text-xs">
                    {quotationStats.totalQuotations}
                  </span>
                )}
              </button>
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {/* Sales Invoices Tab */}
          {activeTab === 'sales' && (
            <>
              <div className="bg-white shadow rounded-md p-4 mb-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Search</label>
                    <input
                      type="text"
                      value={filters.search}
                      onChange={(e) => setFilters({...filters, search: e.target.value})}
                      placeholder="Invoice number, customer name, email..."
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date Range</label>
                    <select
                      value={filters.dateRange}
                      onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm rounded-md"
                    >
                      <option value="all">All Time</option>
                      <option value="today">Today</option>
                      <option value="last7">Last 7 Days</option>
                      <option value="last30">Last 30 Days</option>
                      <option value="last90">Last 90 Days</option>
                      <option value="last365">Last 1 Year</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={() => setFilters({ search: '', dateRange: 'all' })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>

              {stats && (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
                  <StatsCard
                    title="Total Sales Revenue"
                    value={formatCurrency(stats.totalAmount)}
                    icon={
                      <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 1.343-3 3s1.343 3 3 3 3-1.343 3-3-1.343-3-3-3zm0 0c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    }
                  />
                  <StatsCard
                    title="Total GST Collected"
                    value={formatCurrency(stats.totalTaxAmount)}
                    icon={
                      <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    }
                  />
                  <StatsCard
                    title="Total Discounts Given"
                    value={formatCurrency(stats.totalDiscountAmount)}
                    icon={
                      <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                    }
                  />
                  <StatsCard
                    title="Total Invoices"
                    value={stats.totalInvoices}
                    icon={
                      <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    }
                  />
                </div>
              )}

              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <InvoiceTable 
                  invoices={filteredInvoices} 
                  loading={loading}
                  onRefresh={loadSalesData}
                />
              </div>
            </>
          )}

          {/* Purchase Invoices Tab */}
          {activeTab === 'purchases' && (
            <>
              <div className="bg-white shadow rounded-md p-4 mb-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Search</label>
                    <input
                      type="text"
                      value={purchaseFilters.search}
                      onChange={(e) => setPurchaseFilters({...purchaseFilters, search: e.target.value})}
                      placeholder="Invoice number, supplier name, email..."
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date Range</label>
                    <select
                      value={purchaseFilters.dateRange}
                      onChange={(e) => setPurchaseFilters({...purchaseFilters, dateRange: e.target.value})}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm rounded-md"
                    >
                      <option value="all">All Time</option>
                      <option value="today">Today</option>
                      <option value="last7">Last 7 Days</option>
                      <option value="last30">Last 30 Days</option>
                      <option value="last90">Last 90 Days</option>
                      <option value="last365">Last 1 Year</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={() => setPurchaseFilters({ status: '', search: '', dateRange: 'all' })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>

              {purchaseStats && (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 mb-6">
                  <StatsCard
                    title="Total Purchase Amount"
                    value={formatCurrency(purchaseStats.totalAmount)}
                    icon={
                      <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                    }
                  />
                  <StatsCard
                    title="Total GST Paid"
                    value={formatCurrency(purchaseStats.totalGSTPaid)}
                    icon={
                      <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    }
                  />
                  <StatsCard
                    title="Total Purchase Invoices"
                    value={purchaseStats.totalInvoices}
                    icon={
                      <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    }
                  />
                </div>
              )}

              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <PurchaseInvoiceTable 
                  invoices={filteredPurchaseInvoices} 
                  loading={purchaseLoading}
                  onRefresh={loadPurchaseData}
                />
              </div>
            </>
          )}

          {/* Quotations Tab */}
          {activeTab === 'quotations' && (
            <>
              <div className="bg-white shadow rounded-md p-4 mb-6">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Search</label>
                    <input
                      type="text"
                      value={quotationFilters.search}
                      onChange={(e) => setQuotationFilters({...quotationFilters, search: e.target.value})}
                      placeholder="Quotation number, customer name, email..."
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                    />
                  </div>
                 
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Date Range</label>
                    <select
                      value={quotationFilters.dateRange}
                      onChange={(e) => setQuotationFilters({...quotationFilters, dateRange: e.target.value})}
                      className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md"
                    >
                      <option value="all">All Time</option>
                      <option value="today">Today</option>
                      <option value="last7">Last 7 Days</option>
                      <option value="last30">Last 30 Days</option>
                      <option value="last90">Last 90 Days</option>
                      <option value="last365">Last 1 Year</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={() => setQuotationFilters({ status: '', search: '', dateRange: 'all' })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>

              {quotationStats && (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-2 mb-6">
                  <StatsCard
                    title="Total Quotation Value"
                    value={formatCurrency(quotationStats.totalAmount)}
                    icon={
                      <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    }
                  />
                
                  <StatsCard
                    title="Total Quotations"
                    value={quotationStats.totalQuotations}
                    icon={
                      <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    }
                  />
                </div>
              )}

              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <QuotationTable 
                  quotations={filteredQuotations} 
                  loading={quotationLoading}
                  onRefresh={loadQuotationData}
                />
              </div>
            </>
          )}
        </div>

        {/* Create Quotation Modal */}
        {showCreateQuotationModal && (
          <CreateQuotationModal
            onClose={() => setShowCreateQuotationModal(false)}
            onSubmit={handleQuotationCreated}
            onNavigateToAddProduct={handleNavigateToAddProduct}
            isModal={true}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default BillingPage;